/**
 * TicketStageTimeline - Visual stage indicator for ticket progress
 *
 * Shows the current stage of a ticket's lifecycle with visual progression.
 * Stages: Submitted -> Assigned -> Working -> (Waiting) -> Resolved
 */

import { useMemo } from 'react';
import type { Ticket } from '../../../types/ticket';
import { TicketStatus, WaitingReason } from '../../../types/ticket';
import './TicketStageTimeline.css';

interface TicketStageTimelineProps {
  ticket: Ticket;
}

type Stage = 'submitted' | 'assigned' | 'working' | 'waiting' | 'resolved';

interface StageInfo {
  id: Stage;
  label: string;
}

const STAGES: StageInfo[] = [
  { id: 'submitted', label: 'Submitted' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'working', label: 'Working' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'resolved', label: 'Resolved' },
];

export function TicketStageTimeline({ ticket }: TicketStageTimelineProps) {
  const { currentStage, completedStages, isWaitingOnUser, showWaiting } = useMemo(() => {
    let current: Stage = 'submitted';
    const completed: Stage[] = [];
    let waitingOnUser = false;
    let showWaitingStage = false;

    switch (ticket.status) {
      case TicketStatus.NEW:
        if (ticket.assignedTechId) {
          current = 'assigned';
          completed.push('submitted');
        } else {
          current = 'submitted';
        }
        break;
      case TicketStatus.IN_PROGRESS:
        current = 'working';
        completed.push('submitted', 'assigned');
        break;
      case TicketStatus.WAITING:
        current = 'waiting';
        completed.push('submitted', 'assigned', 'working');
        waitingOnUser = ticket.waitingReason === WaitingReason.USER;
        showWaitingStage = true;
        break;
      case TicketStatus.RESOLVED:
      case TicketStatus.CLOSED:
        current = 'resolved';
        completed.push('submitted', 'assigned', 'working', 'resolved');
        break;
    }

    return {
      currentStage: current,
      completedStages: completed,
      isWaitingOnUser: waitingOnUser,
      showWaiting: showWaitingStage,
    };
  }, [ticket]);

  const visibleStages = useMemo(() => {
    if (showWaiting) {
      return STAGES;
    }
    return STAGES.filter((stage) => stage.id !== 'waiting');
  }, [showWaiting]);

  return (
    <div className="ticket-stage-timeline">
      <h3 className="ticket-stage-timeline__heading">Request Progress</h3>
      <div className="ticket-stage-timeline__stages">
        {visibleStages.map((stage, index) => {
          const isCompleted = completedStages.includes(stage.id);
          const isCurrent = currentStage === stage.id;
          const isActionRequired = stage.id === 'waiting' && isWaitingOnUser;

          return (
            <div
              key={stage.id}
              className={`ticket-stage-timeline__stage ${
                isCompleted ? 'ticket-stage-timeline__stage--completed' : ''
              } ${isCurrent ? 'ticket-stage-timeline__stage--current' : ''} ${
                isActionRequired ? 'ticket-stage-timeline__stage--action-required' : ''
              }`}
            >
              {index > 0 && (
                <div
                  className={`ticket-stage-timeline__connector ${
                    isCompleted || isCurrent ? 'ticket-stage-timeline__connector--active' : ''
                  }`}
                />
              )}

              <div className="ticket-stage-timeline__indicator">
                {isCompleted ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm3.78 5.28l-5 5a.75.75 0 01-1.06 0l-2-2a.75.75 0 111.06-1.06L6.25 8.69l4.47-4.47a.75.75 0 111.06 1.06z" />
                  </svg>
                ) : isCurrent ? (
                  <div className="ticket-stage-timeline__pulse" />
                ) : (
                  <div className="ticket-stage-timeline__dot" />
                )}
              </div>

              <div className="ticket-stage-timeline__info">
                <span className="ticket-stage-timeline__label">{stage.label}</span>
                {isCurrent && isActionRequired && (
                  <span className="ticket-stage-timeline__action-badge">Your response needed</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
